import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fdf9 0%, #e8f5e9 100%);
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Breadcrumbs = styled.nav`
  margin-bottom: 30px;
  font-size: 14px;
  color: #666;

  a {
    color: #22C55E;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }

  span {
    margin: 0 8px;
    color: #999;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
`;

const AppBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #22C55E;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #1a1a1a;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  color: #666;
  font-size: 14px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 40px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const TableOfContents = styled.div`
  background: #f8fdf9;
  border-left: 4px solid #22C55E;
  padding: 20px 25px;
  border-radius: 0 8px 8px 0;
  margin-bottom: 30px;

  h3 {
    color: #1a1a1a;
    margin-bottom: 15px;
    font-size: 1.1rem;
  }

  ol {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  a {
    color: #22C55E;
    text-decoration: none;
    font-size: 15px;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Section = styled.section`
  margin-bottom: 35px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  color: #1a1a1a;
  font-size: 1.4rem;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e8f5e9;
  display: flex;
  align-items: center;
  gap: 10px;

  .icon {
    color: #22C55E;
  }
`;

const Paragraph = styled.p`
  color: #444;
  line-height: 1.8;
  margin-bottom: 15px;
  font-size: 15px;
`;

const List = styled.ul`
  color: #444;
  line-height: 1.8;
  margin-bottom: 15px;
  padding-left: 25px;

  li {
    margin-bottom: 10px;
    font-size: 15px;
  }
`;

const HighlightBox = styled.div`
  background: ${props => {
    switch(props.variant) {
      case 'warning': return '#fef3c7';
      case 'danger': return '#fee2e2';
      case 'info': return '#e0f2fe';
      default: return '#f0fdf4';
    }
  }};
  border-left: 4px solid ${props => {
    switch(props.variant) {
      case 'warning': return '#f59e0b';
      case 'danger': return '#ef4444';
      case 'info': return '#0ea5e9';
      default: return '#22C55E';
    }
  }};
  padding: 15px 20px;
  border-radius: 0 8px 8px 0;
  margin: 20px 0;

  p {
    margin: 0;
    color: #444;
    font-size: 14px;
  }

  strong {
    color: #1a1a1a;
  }
`;

const PricingTable = styled.div`
  overflow-x: auto;
  margin: 20px 0;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    th, td {
      padding: 12px 15px;
      text-align: left;
      border-bottom: 1px solid #e8f5e9;
    }

    th {
      background: #f0fdf4;
      color: #1a1a1a;
      font-weight: 600;
    }

    td {
      color: #444;
    }

    tr:hover td {
      background: #f8fdf9;
    }
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, #22C55E 0%, #16a34a 100%);
  color: white;
  padding: 25px;
  border-radius: 12px;
  text-align: center;

  h3 {
    margin-bottom: 15px;
    font-size: 1.2rem;
  }

  p {
    margin-bottom: 10px;
    font-size: 15px;
  }

  a {
    color: white;
    font-weight: 600;
  }
`;

const RelatedLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  flex-wrap: wrap;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #f0fdf4;
    color: #22C55E;
    padding: 12px 20px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      background: #22C55E;
      color: white;
    }
  }
`;

export const TermsOfServicePage = () => {
  return (
    <>
      <Navbar />
      <PageContainer>
        <ContentWrapper>
          <Breadcrumbs>
            <Link to="/">Inicio</Link>
            <span>›</span>
            <span>Términos de Servicio - PlantSnap AI</span>
          </Breadcrumbs>

          <Header>
            <AppBadge>
              🌿 PlantSnap AI
            </AppBadge>
            <Title>Términos de Servicio</Title>
            <LastUpdated>Última actualización: 24 de enero de 2026</LastUpdated>
          </Header>

          <Card>
            <TableOfContents>
              <h3>📋 Tabla de Contenidos</h3>
              <ol>
                <li><a href="#aceptacion">Aceptación de los Términos</a></li>
                <li><a href="#descripcion">Descripción del Servicio</a></li>
                <li><a href="#cuentas">Cuentas de Usuario</a></li>
                <li><a href="#suscripciones">Suscripciones y Pagos</a></li>
                <li><a href="#creditos">Sistema de Créditos</a></li>
                <li><a href="#uso-aceptable">Uso Aceptable</a></li>
                <li><a href="#contenido-usuario">Contenido del Usuario</a></li>
                <li><a href="#propiedad-intelectual">Propiedad Intelectual</a></li>
                <li><a href="#limitacion">Limitación de Responsabilidad</a></li>
                <li><a href="#garantias">Exclusión de Garantías</a></li>
                <li><a href="#indemnizacion">Indemnización</a></li>
                <li><a href="#terminacion">Terminación</a></li>
                <li><a href="#modificaciones">Modificaciones</a></li>
                <li><a href="#ley-aplicable">Ley Aplicable</a></li>
                <li><a href="#contacto">Contacto</a></li>
              </ol>
            </TableOfContents>

            <Section id="aceptacion">
              <SectionTitle>
                <span className="icon">1.</span> Aceptación de los Términos
              </SectionTitle>
              <Paragraph>
                Estos Términos de Servicio ("Términos") constituyen un acuerdo legalmente vinculante entre 
                usted ("Usuario" o "usted") y <strong>Monaco IT Services</strong> ("Empresa", "nosotros" o "nos"), 
                con domicilio en Chile, respecto al uso de la aplicación móvil PlantSnap AI y sus servicios 
                relacionados.
              </Paragraph>
              <Paragraph>
                Al descargar, instalar, acceder o utilizar PlantSnap AI (Bundle ID: com.martinseguel.plantsnapid), 
                usted confirma que:
              </Paragraph>
              <List>
                <li>Ha leído y comprendido estos Términos en su totalidad</li>
                <li>Acepta quedar vinculado por estos Términos</li>
                <li>Tiene al menos 13 años de edad (o 16 en jurisdicciones aplicables)</li>
                <li>Tiene capacidad legal para celebrar este acuerdo</li>
              </List>
              <HighlightBox variant="warning">
                <p>
                  <strong>⚠️ Importante:</strong> Si no está de acuerdo con estos Términos, no debe usar 
                  PlantSnap AI. El uso continuado de la aplicación constituye su aceptación de cualquier 
                  modificación a estos Términos.
                </p>
              </HighlightBox>
            </Section>

            <Section id="descripcion">
              <SectionTitle>
                <span className="icon">2.</span> Descripción del Servicio
              </SectionTitle>
              <Paragraph>
                PlantSnap AI es una aplicación móvil que utiliza inteligencia artificial para:
              </Paragraph>
              <List>
                <li><strong>Identificar plantas:</strong> Los usuarios pueden tomar o subir fotos de plantas, y nuestra IA intentará identificar la especie.</li>
                <li><strong>Detectar enfermedades:</strong> Análisis de posibles problemas de salud en las plantas.</li>
                <li><strong>Proporcionar consejos de cuidado:</strong> Recomendaciones personalizadas basadas en la especie identificada.</li>
                <li><strong>Jardín virtual:</strong> Guardar y organizar las plantas identificadas.</li>
                <li><strong>Historial:</strong> Mantener un registro de todas las identificaciones realizadas.</li>
              </List>
              <Paragraph>
                El servicio está disponible para dispositivos iOS y Android a través de App Store y Google Play Store respectivamente.
              </Paragraph>
            </Section>

            <Section id="cuentas">
              <SectionTitle>
                <span className="icon">3.</span> Cuentas de Usuario
              </SectionTitle>
              <Paragraph>
                Para acceder a ciertas funcionalidades de PlantSnap AI, debe crear una cuenta. Al hacerlo, usted acepta:
              </Paragraph>
              <List>
                <li>Proporcionar información precisa, actual y completa durante el registro</li>
                <li>Mantener actualizada la información de su cuenta</li>
                <li>Mantener la confidencialidad de sus credenciales de acceso</li>
                <li>Ser responsable de toda actividad que ocurra bajo su cuenta</li>
                <li>Notificarnos inmediatamente sobre cualquier uso no autorizado</li>
              </List>
              <Paragraph>
                Nos reservamos el derecho de suspender o terminar cuentas que violen estos Términos o que 
                permanezcan inactivas por períodos prolongados.
              </Paragraph>
            </Section>

            <Section id="suscripciones">
              <SectionTitle>
                <span className="icon">4.</span> Suscripciones y Pagos
              </SectionTitle>
              
              <PricingTable>
                <table>
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Créditos</th>
                      <th>Características</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Gratuito</strong></td>
                      <td>2 créditos de por vida</td>
                      <td>Funcionalidades básicas, historial limitado</td>
                    </tr>
                    <tr>
                      <td><strong>Pro Semanal</strong></td>
                      <td>14 créditos semanales</td>
                      <td>Todas las funcionalidades, historial completo, sin anuncios</td>
                    </tr>
                    <tr>
                      <td><strong>Pro Anual</strong></td>
                      <td>14 créditos semanales</td>
                      <td>Todas las funcionalidades + ahorro significativo</td>
                    </tr>
                  </tbody>
                </table>
              </PricingTable>

              <Paragraph>
                <strong>Términos de pago:</strong>
              </Paragraph>
              <List>
                <li>Los pagos se procesan a través de App Store (iOS) o Google Play Store (Android)</li>
                <li>Las suscripciones se renuevan automáticamente hasta que se cancelen</li>
                <li>El cobro se realiza a través de su cuenta de Apple ID o Google Play</li>
                <li>Los precios pueden variar según su ubicación y pueden cambiar con aviso previo</li>
              </List>

              <HighlightBox variant="info">
                <p>
                  <strong>💳 Política de reembolsos:</strong> Los reembolsos están sujetos a las políticas 
                  de Apple App Store y Google Play Store. Para solicitar un reembolso, debe contactar 
                  directamente a la tienda correspondiente. No tenemos control sobre las decisiones de 
                  reembolso de las plataformas.
                </p>
              </HighlightBox>

              <Paragraph>
                <strong>Cancelación:</strong>
              </Paragraph>
              <List>
                <li>Puede cancelar su suscripción en cualquier momento</li>
                <li>La cancelación debe realizarse al menos 24 horas antes del próximo cobro</li>
                <li>Tras cancelar, mantendrá el acceso hasta el final del período pagado</li>
                <li>Los créditos no utilizados no son reembolsables ni transferibles</li>
              </List>
            </Section>

            <Section id="creditos">
              <SectionTitle>
                <span className="icon">5.</span> Sistema de Créditos
              </SectionTitle>
              <Paragraph>
                PlantSnap AI utiliza un sistema de créditos para las identificaciones:
              </Paragraph>
              <List>
                <li><strong>1 crédito = 1 identificación de planta</strong></li>
                <li>Los usuarios gratuitos reciben 2 créditos de por vida (únicos, no renovables)</li>
                <li>Los usuarios Pro reciben 14 créditos cada semana</li>
                <li>Los créditos se renuevan semanalmente para suscriptores activos</li>
                <li>Los créditos no utilizados no se acumulan de una semana a otra</li>
                <li>Los créditos no son transferibles entre cuentas</li>
                <li>No se realizan reembolsos por créditos no utilizados</li>
              </List>
            </Section>

            <Section id="uso-aceptable">
              <SectionTitle>
                <span className="icon">6.</span> Uso Aceptable
              </SectionTitle>
              <Paragraph>
                Al usar PlantSnap AI, usted acepta NO:
              </Paragraph>
              <List>
                <li>Usar la aplicación para identificar plantas con fines ilegales o dañinos</li>
                <li>Intentar identificar plantas con intención de usar información tóxica para causar daño a personas o animales</li>
                <li>Subir contenido inapropiado, ofensivo, ilegal o que viole derechos de terceros</li>
                <li>Intentar hackear, descompilar o realizar ingeniería inversa de la aplicación</li>
                <li>Usar bots, scripts o métodos automatizados para acceder al servicio</li>
                <li>Eludir las limitaciones de créditos o funcionalidades</li>
                <li>Compartir su cuenta con terceros o vender acceso a ella</li>
                <li>Usar el servicio para competir directamente con nosotros</li>
                <li>Interferir con el funcionamiento normal del servicio</li>
                <li>Recopilar información de otros usuarios sin su consentimiento</li>
              </List>

              <HighlightBox variant="danger">
                <p>
                  <strong>🚨 Advertencia sobre plantas tóxicas:</strong> Está estrictamente prohibido usar 
                  PlantSnap AI para identificar plantas venenosas o tóxicas con la intención de causar 
                  daño a personas, animales o el medio ambiente. Cualquier uso indebido será reportado 
                  a las autoridades competentes y resultará en la terminación inmediata de su cuenta.
                </p>
              </HighlightBox>
            </Section>

            <Section id="contenido-usuario">
              <SectionTitle>
                <span className="icon">7.</span> Contenido del Usuario
              </SectionTitle>
              <Paragraph>
                Al subir fotos u otro contenido a PlantSnap AI:
              </Paragraph>
              <List>
                <li>Usted mantiene la propiedad de su contenido original</li>
                <li>Nos otorga una licencia mundial, no exclusiva, libre de regalías para usar, almacenar y procesar su contenido con el fin de proporcionar el servicio</li>
                <li>Garantiza que tiene los derechos necesarios para compartir dicho contenido</li>
                <li>Acepta que las fotos serán procesadas por sistemas de IA de terceros (OpenAI)</li>
              </List>
              <Paragraph>
                Podemos usar datos agregados y anonimizados derivados del uso de la aplicación para mejorar 
                nuestros servicios, sin identificar individualmente a los usuarios.
              </Paragraph>
            </Section>

            <Section id="propiedad-intelectual">
              <SectionTitle>
                <span className="icon">8.</span> Propiedad Intelectual
              </SectionTitle>
              <Paragraph>
                <strong>Nuestra propiedad:</strong>
              </Paragraph>
              <List>
                <li>PlantSnap AI, incluyendo su código, diseño, logotipos, marcas y contenido, es propiedad de Monaco IT Services</li>
                <li>Todos los derechos de propiedad intelectual están reservados</li>
                <li>Se le otorga una licencia limitada, personal y no transferible para usar la aplicación</li>
              </List>
              <Paragraph>
                <strong>Restricciones:</strong>
              </Paragraph>
              <List>
                <li>No puede copiar, modificar, distribuir o vender ninguna parte de la aplicación</li>
                <li>No puede usar nuestras marcas sin autorización escrita</li>
                <li>No puede eliminar avisos de copyright o propiedad</li>
              </List>
            </Section>

            <Section id="limitacion">
              <SectionTitle>
                <span className="icon">9.</span> Limitación de Responsabilidad
              </SectionTitle>
              
              <HighlightBox variant="danger">
                <p>
                  <strong>⚠️ AVISO IMPORTANTE:</strong> PlantSnap AI utiliza inteligencia artificial para 
                  identificar plantas. LA IA PUEDE EQUIVOCARSE. Las identificaciones proporcionadas son 
                  orientativas y no deben utilizarse como única fuente para decisiones que puedan afectar 
                  la salud, seguridad o bienestar de personas o animales.
                </p>
              </HighlightBox>

              <Paragraph>
                En la máxima medida permitida por la ley aplicable:
              </Paragraph>
              <List>
                <li>No garantizamos la exactitud de las identificaciones de plantas</li>
                <li>No somos responsables de decisiones tomadas basándose en información proporcionada por la app</li>
                <li>No somos responsables de daños directos, indirectos, incidentales, especiales o consecuentes</li>
                <li>Nuestra responsabilidad total está limitada al monto pagado por usted en los últimos 12 meses</li>
                <li>No somos responsables de la pérdida de datos o interrupciones del servicio</li>
              </List>

              <HighlightBox variant="warning">
                <p>
                  <strong>🌿 Antes de consumir o manipular cualquier planta:</strong> Siempre consulte con 
                  un experto botánico o profesional de la salud. Nunca consuma una planta basándose 
                  únicamente en la identificación de una aplicación.
                </p>
              </HighlightBox>
            </Section>

            <Section id="garantias">
              <SectionTitle>
                <span className="icon">10.</span> Exclusión de Garantías
              </SectionTitle>
              <Paragraph>
                PlantSnap AI se proporciona "TAL CUAL" y "SEGÚN DISPONIBILIDAD", sin garantías de ningún tipo, 
                expresas o implícitas, incluyendo pero no limitado a:
              </Paragraph>
              <List>
                <li>Garantías de comerciabilidad</li>
                <li>Idoneidad para un propósito particular</li>
                <li>No infracción de derechos</li>
                <li>Disponibilidad continua o ininterrumpida</li>
                <li>Ausencia de errores o virus</li>
                <li>Exactitud de los resultados</li>
              </List>
            </Section>

            <Section id="indemnizacion">
              <SectionTitle>
                <span className="icon">11.</span> Indemnización
              </SectionTitle>
              <Paragraph>
                Usted acepta defender, indemnizar y mantener indemne a Monaco IT Services, sus directores, 
                empleados y agentes de cualquier reclamación, daño, pérdida, responsabilidad, costo o gasto 
                (incluyendo honorarios legales razonables) que surjan de:
              </Paragraph>
              <List>
                <li>Su uso de PlantSnap AI</li>
                <li>Su violación de estos Términos</li>
                <li>Su violación de derechos de terceros</li>
                <li>El contenido que usted suba a la aplicación</li>
              </List>
            </Section>

            <Section id="terminacion">
              <SectionTitle>
                <span className="icon">12.</span> Terminación
              </SectionTitle>
              <Paragraph>
                <strong>Terminación por usted:</strong>
              </Paragraph>
              <List>
                <li>Puede eliminar su cuenta en cualquier momento desde Configuración &gt; Cuenta &gt; Eliminar cuenta</li>
                <li>Puede dejar de usar la aplicación y desinstalarla</li>
                <li>La cancelación de suscripción no elimina automáticamente su cuenta</li>
              </List>

              <Paragraph>
                <strong>Terminación por nosotros:</strong>
              </Paragraph>
              <List>
                <li>Podemos suspender o terminar su cuenta por violación de estos Términos</li>
                <li>Podemos terminar el servicio con aviso previo de 30 días</li>
                <li>En caso de violaciones graves, la terminación puede ser inmediata</li>
              </List>

              <Paragraph>
                <strong>Efectos de la terminación:</strong>
              </Paragraph>
              <List>
                <li>Perderá acceso a su cuenta y datos asociados</li>
                <li>Los créditos no utilizados se perderán</li>
                <li>Las secciones sobre limitación de responsabilidad, propiedad intelectual e indemnización sobrevivirán</li>
              </List>
            </Section>

            <Section id="modificaciones">
              <SectionTitle>
                <span className="icon">13.</span> Modificaciones
              </SectionTitle>
              <Paragraph>
                Nos reservamos el derecho de modificar estos Términos en cualquier momento. Los cambios 
                entrarán en vigor:
              </Paragraph>
              <List>
                <li>Inmediatamente para nuevos usuarios</li>
                <li>30 días después de la notificación para usuarios existentes</li>
                <li>Notificaremos cambios significativos por email o dentro de la aplicación</li>
              </List>
              <Paragraph>
                El uso continuado del servicio después de los cambios constituye su aceptación de los 
                Términos modificados.
              </Paragraph>
            </Section>

            <Section id="ley-aplicable">
              <SectionTitle>
                <span className="icon">14.</span> Ley Aplicable y Jurisdicción
              </SectionTitle>
              <Paragraph>
                Estos Términos se regirán e interpretarán de acuerdo con las leyes de la República de Chile, 
                sin consideración a sus disposiciones sobre conflictos de leyes.
              </Paragraph>
              <Paragraph>
                Cualquier disputa que surja de estos Términos o del uso de PlantSnap AI será sometida a la 
                jurisdicción exclusiva de los tribunales ordinarios de justicia de Santiago, Chile.
              </Paragraph>
              <Paragraph>
                <strong>Disposiciones adicionales:</strong>
              </Paragraph>
              <List>
                <li><strong>Divisibilidad:</strong> Si alguna disposición es inválida, las demás permanecerán en vigor</li>
                <li><strong>Renuncia:</strong> La falta de ejercicio de un derecho no constituye renuncia al mismo</li>
                <li><strong>Acuerdo completo:</strong> Estos Términos constituyen el acuerdo completo entre las partes</li>
                <li><strong>Cesión:</strong> No puede ceder sus derechos sin nuestro consentimiento escrito</li>
              </List>
            </Section>

            <Section id="contacto">
              <SectionTitle>
                <span className="icon">15.</span> Contacto
              </SectionTitle>
              <Paragraph>
                Si tiene preguntas sobre estos Términos de Servicio, puede contactarnos:
              </Paragraph>
              <ContactCard>
                <h3>Monaco IT Services</h3>
                <p>📧 Email: <a href="mailto:support@plantsnap.app">support@plantsnap.app</a></p>
                <p>🌐 Sitio web: <a href="https://monaco-it-services.cl" target="_blank" rel="noopener noreferrer">monaco-it-services.cl</a></p>
                <p>📍 País: Chile</p>
              </ContactCard>
            </Section>

            <RelatedLinks>
              <Link to="/plantsnapai/privacy">
                🔒 Política de Privacidad
              </Link>
              <Link to="/">
                🏠 Volver al Inicio
              </Link>
            </RelatedLinks>
          </Card>
        </ContentWrapper>
      </PageContainer>
      <Footer />
    </>
  );
};
